import { useState, useEffect, useCallback } from "react";
import { fetchCardEbayData } from "./services";
import { psaPricesFromEbayPayload } from "./utils";
import { save, drop } from "./stamps";
import { GRADED_ENABLED } from "./constants";

const COLLECTION_STORAGE_KEY = "ppt_collection_v1";
const PERCENTAGE_STORAGE_KEY = "ppt_percentage_v1";

// The percentage is a standing preference — what you're willing to pay against
// market — not something you retype every visit. Kept in range so a stored 0 or
// a hand-edited nonsense value can't wipe out the totals.
function loadPercentage() {
  try {
    const raw = window.localStorage.getItem(PERCENTAGE_STORAGE_KEY);
    if (raw == null) return 100;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0 || n > 1000) return 100;
    return n;
  } catch {
    return 100;
  }
}

// Get the display price for a card based on whether it's raw or a PSA slab
/**
 * What one card is worth, or null when nobody knows.
 *
 * Null rather than zero, and null rather than a guess. Zero is a price — a
 * card worth nothing — and a collection total that quietly counted unknown
 * cards as free would read as a fact about the collection. And the guess
 * this used to make below Near Mint, a flat percentage of the NM price, was
 * checked against a Charizard and found wrong by enough to matter.
 *
 * In order: a graded sale for the slab; a real quote for this condition; the
 * price the owner typed for it; and for Near Mint only, the market price,
 * which is what "market" means at every source this app reads.
 */
function getCardPrice(card) {
  // A slab priced before graded prices were switched off is a raw card now;
  // its stored graded figure would otherwise keep counting in the total.
  if (GRADED_ENABLED && card.isSlab && card.psaGrade && card.psaPrices) {
    const price = card.psaPrices[card.psaGrade];
    if (price != null && Number.isFinite(Number(price))) return Number(price);
  }
  const quoted = card.prices?.[card.condition];
  if (quoted && quoted.estimated !== true && Number.isFinite(Number(quoted.price))) {
    return Number(quoted.price);
  }
  const own = Number(card.ownPrice);
  if (Number.isFinite(own) && own > 0) return own;
  if ((card.condition || "Near Mint") === "Near Mint") {
    const market = Number(card.marketPrice);
    if (Number.isFinite(market) && market > 0) return market;
  }
  return null;
}

/** True when the card sits in a condition nobody has priced. */
export const needsPrice = (card) => getCardPrice(card) === null;

export default function useCollection() {
  const [collection, setCollection] = useState([]);
  const [percentage, setPercentage] = useState(loadPercentage);
  const [collectionDebug, setCollectionDebug] = useState([]);

  // Saving waits for the load to finish. A ref isn't enough: it flips to true
  // inside the load effect, and the save effect then runs in the same commit
  // still holding the initial empty array, wiping the stored collection.
  // StrictMode re-runs the load effect straight after, so it read that empty
  // state back. State gates the save until the loaded cards are committed.
  const [ready, setReady] = useState(false);

  // Hydrate from localStorage on first load
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(COLLECTION_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setCollection(parsed);
        }
      }
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Failed to load collection from localStorage", e);
      }
    }
    setReady(true);
  }, []);

  // Persist to localStorage whenever collection changes
  useEffect(() => {
    if (!ready) return;
    try {
      if (!Array.isArray(collection) || collection.length === 0) {
        drop(COLLECTION_STORAGE_KEY);
        return;
      }
      save(COLLECTION_STORAGE_KEY, JSON.stringify(collection));
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Failed to save collection to localStorage", e);
      }
    }
  }, [collection, ready]);

  useEffect(() => {
    try {
      save(PERCENTAGE_STORAGE_KEY, String(percentage));
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Failed to save percentage to localStorage", e);
      }
    }
  }, [percentage]);

  const logEvent = useCallback((evt) => {
    const event = { when: new Date().toISOString(), ...evt };
    setCollectionDebug((prev) => [event, ...prev].slice(0, 200));
  }, []);

  /* Cards carry the game they belong to so the collection can be shown one
     game at a time — a One Piece card has no condition and no graded price, so
     mixing it into a Pokemon total would put uncosted cards beside costed ones.
     Anything saved before this field existed is Pokemon, which is all there
     was. */
  const addToCollection = useCallback((card, game = "pokemon") => {
    setCollection((prev) => {
      if (prev.some((c) => c.id === card.id)) {
        logEvent({ action: "add_duplicate_skipped", id: card.id, name: card.name, number: card.number, setId: card.setId, setName: card.setName });
        return prev;
      }
      const defaultCondition = "Near Mint";
      const price = card?.prices?.[defaultCondition]?.price ?? card?.marketPrice ?? 0;
      logEvent({ action: "add", id: card.id, name: card.name, number: card.number, setId: card.setId, setName: card.setName, condition: defaultCondition, qty: 1, price });
      return [...prev, { ...card, game, qty: 1, condition: defaultCondition, isSlab: false, psaGrade: null, psaPrices: null }];
    });
  }, [logEvent]);

  /* The collection had no way out and no way back in, so it lived in one
     browser and died with it — while lists, which matter less, could be
     exported. Merges rather than replaces: importing a backup onto a device
     that already has cards should not throw the newer ones away. */
  const importCards = useCallback((incoming) => {
    if (!Array.isArray(incoming)) return 0;

    // Counted here rather than inside the updater: React runs that when it
    // chooses, so a count assigned in there is still zero by the time the
    // caller reads it — and the caller is what tells you how many landed.
    const have = new Set(collection.map((c) => String(c.id)));
    const extra = [];
    for (const raw of incoming) {
      if (!raw || raw.id == null) continue;
      const id = String(raw.id);
      if (have.has(id)) continue;
      have.add(id);
      extra.push({
        ...raw,
        id,
        qty: Math.min(99, Math.max(1, Number(raw.qty) || 1)),
        condition: raw.condition || "Near Mint",
        isSlab: raw.isSlab === true,
        psaGrade: raw.psaGrade ?? null,
        // Prices in a file are stale the day after it was written, and the
        // grade panel refetches on demand anyway.
        psaPrices: null,
        psaState: undefined,
      });
    }

    if (extra.length) {
      // Deduped again at commit time against whatever is actually there.
      setCollection((prev) => {
        const seen = new Set(prev.map((c) => String(c.id)));
        return [...prev, ...extra.filter((c) => !seen.has(c.id))];
      });
    }
    logEvent({ action: "import", offered: incoming.length, added: extra.length });
    return extra.length;
  }, [collection, logEvent]);

  /**
   * @returns { card, index } so the removal can be taken back exactly — same
   *          place, same quantity, same everything — or null if nothing went.
   */
  const removeFromCollection = useCallback((id) => {
    const index = collection.findIndex((c) => c.id === id);
    const found = index >= 0 ? collection[index] : null;
    if (found) {
      logEvent({ action: "remove", id: found.id, name: found.name, number: found.number, setId: found.setId, setName: found.setName });
    } else {
      logEvent({ action: "remove_not_found", id });
    }
    setCollection((prev) => prev.filter((c) => c.id !== id));
    return found ? { card: found, index } : null;
  }, [collection, logEvent]);

  /** The other half of removeFromCollection: back where it was, as it was. */
  const restoreToCollection = useCallback((card, index) => {
    setCollection((prev) => {
      if (prev.some((c) => c.id === card.id)) return prev;
      const at = Math.min(Math.max(0, index), prev.length);
      return [...prev.slice(0, at), card, ...prev.slice(at)];
    });
    logEvent({ action: "restore", id: card.id, name: card.name });
  }, [logEvent]);

  const updateQty = useCallback((id, qty) => {
    const newQty = Math.min(99, Math.max(1, Number(qty)));
    setCollection((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        logEvent({ action: "update_qty", id, name: c.name, from: c.qty, to: newQty });
        return { ...c, qty: newQty };
      })
    );
  }, [logEvent]);

  const updateCondition = useCallback((id, condition) => {
    setCollection((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        logEvent({ action: "update_condition", id, name: c.name, from: c.condition, to: condition });
        return { ...c, condition };
      })
    );
  }, [logEvent]);

  // Fetch eBay graded data and extract PSA prices for a card
  const fetchPsaForCard = useCallback(async (card) => {
    try {
      const data = await fetchCardEbayData(card.id);
      const psaPrices = psaPricesFromEbayPayload(data);
      // The whole API response used to be kept here, for up to 200 events —
      // megabytes of card data held purely so a debug drawer could print it.
      logEvent({
        action: "psa_fetch_result",
        id: card.id,
        hasPsaPrices: !!psaPrices,
        psaPrices,
      });
      return psaPrices;
    } catch (e) {
      logEvent({ action: "psa_fetch_error", id: card.id, error: e?.response?.status || e.message, details: e?.response?.data || e.message });
      return null;
    }
  }, [logEvent]);

  // Toggle a card between raw and PSA slab mode
  const toggleSlab = useCallback(async (id) => {
    const card = collection.find((c) => c.id === id);
    if (!card) return;

    const turningOn = !card.isSlab;

    const needsFetch = turningOn && !card.psaPrices && card.psaState !== "none";

    setCollection((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              isSlab: turningOn,
              psaGrade: turningOn ? (c.psaGrade || 10) : c.psaGrade,
              ...(needsFetch ? { psaState: "loading" } : {}),
            }
          : c
      )
    );
    logEvent({ action: "toggle_slab", id, name: card.name, isSlab: turningOn });

    // Fetch PSA pricing if enabling slab mode and we don't have prices yet.
    // A card with no graded sales has to record that it looked, or the tile
    // shows the raw price with no hint that slab mode found nothing.
    if (needsFetch) {
      const psaPrices = await fetchPsaForCard(card);
      setCollection((prev) =>
        prev.map((c) =>
          c.id === id ? { ...c, psaPrices: psaPrices || null, psaState: psaPrices ? "ready" : "none" } : c
        )
      );
    }
  }, [collection, logEvent, fetchPsaForCard]);

  // Update PSA grade for a slab
  const updatePsaGrade = useCallback((id, grade) => {
    const newGrade = Number(grade);
    setCollection((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        logEvent({ action: "update_psa_grade", id, name: c.name, from: c.psaGrade, to: newGrade });
        return { ...c, psaGrade: newGrade };
      })
    );
  }, [logEvent]);

  // Fetch PSA pricing for a card (can be called from search results too)
  const loadPsaPricing = useCallback(async (tcgPlayerId) => {
    try {
      return psaPricesFromEbayPayload(await fetchCardEbayData(tcgPlayerId));
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Failed to fetch eBay data for", tcgPlayerId, e);
      }
      return null;
    }
  }, []);

  /* The total counts what has a price and says how many cards do not. It
     used to count them at zero, silently — a figure that looked like the
     value of the collection and was the value of part of it. */
  const { totalValue, unpricedCount } = collection.reduce(
    (acc, c) => {
      const price = getCardPrice(c);
      if (price === null) acc.unpricedCount += 1;
      else acc.totalValue += price * c.qty;
      return acc;
    },
    { totalValue: 0, unpricedCount: 0 }
  );

  /** The owner's own figure for a card in a condition nobody quotes. */
  const setOwnPrice = useCallback((id, price) => {
    const value = Number(price);
    setCollection((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        if (!Number.isFinite(value) || value <= 0) {
          const { ownPrice, ...rest } = c;
          return rest;
        }
        return { ...c, ownPrice: Math.round(value * 100) / 100 };
      })
    );
  }, []);

  // Cards saved before the field existed are Pokemon, which is all there was.
  const forGame = useCallback(
    (game) => collection.filter((c) => (c.game || "pokemon") === game),
    [collection]
  );

  return {
    collection,
    forGame,
    addToCollection,
    importCards,
    removeFromCollection,
    restoreToCollection,
    updateQty,
    updateCondition,
    toggleSlab,
    updatePsaGrade,
    loadPsaPricing,
    getCardPrice,
    setOwnPrice,
    totalValue,
    unpricedCount,
    percentage,
    setPercentage,
    collectionDebug,
  };
}
